import React, { useState, ReactNode } from "react"; // ReactNode 추가
import styled from "styled-components";
import CustomInput from "./common/CustomInput";
import CustomButton from "./common/CustomButton";
import { snackbarStore } from "src/store/snackbarStore";

type LoginType = "user" | "vendor";

interface LoginSectionProps {
  onSubmit: (id: string, password: string, type: LoginType) => void;
  children?: ReactNode;
  loading?: boolean;
  error?: string | null;
}

const LoginSection: React.FC<LoginSectionProps> = ({
  onSubmit,
  children,
  loading = false,
  error,
}) => {
  // 2. props에서 children 추출
  const [loginType, setLoginType] = useState<LoginType>("user");
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");

  const handleLoginClick = () => {
    if (!id || !password) {
      snackbarStore.getState().addSnackbar({
        message: "아이디와 비밀번호를 모두 입력해주세요.",
        severity: "error",
      });
      return;
    }
    onSubmit(id, password, loginType);
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLoginClick();
    }
  };

  return (
    <Wrapper>
      <Title>놀로 KNOLLO</Title>

      <Segmented role="tablist" aria-label="로그인 타입 선택">
        <SegButton
          type="button"
          aria-selected={loginType === "user"}
          $active={loginType === "user"}
          onClick={() => setLoginType("user")}
        >
          일반 로그인
        </SegButton>
        <SegButton
          type="button"
          aria-selected={loginType === "vendor"}
          $active={loginType === "vendor"}
          onClick={() => setLoginType("vendor")}
        >
          입점사 로그인
        </SegButton>
      </Segmented>

      {loginType === "user" ? (
        <>{children && <DividerContainer>{children}</DividerContainer>}</>
      ) : (
        <>
          <CustomInput
            label={loginType === "vendor" ? "이메일" : "아이디"}
            value={id}
            onChange={(e) => setId(e.target.value)}
            id="login-id"
            type={loginType === "vendor" ? "email" : "text"}
            placeholder={loginType === "vendor" ? "이메일" : "아이디"}
            onKeyDown={onEnter}
          />
          <CustomInput
            label="비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            id="login-password"
            type="password"
            placeholder="비밀번호"
            onKeyDown={onEnter}
          />

          <Helper>
            {loginType === "vendor"
              ? "입점사 전용 계정으로 로그인하세요."
              : "일반 회원 계정으로 로그인하세요."}
          </Helper>

          {error && <ErrorText>{error}</ErrorText>}

          <CustomButton
            onClick={handleLoginClick}
            variant="contained"
            fullWidth
            isLoading={loading}
            disabled={loading}
          >
            {loading ? "로그인 중..." : "로그인"}
          </CustomButton>
        </>
      )}
    </Wrapper>
  );
};

export default LoginSection;
const Wrapper = styled.div`
  width: 400px;
  padding: 32px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
`;

const Segmented = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #f5f6f7;
  padding: 4px;
  border-radius: 10px;
  gap: 4px;
`;

const SegButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#546E7A" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#333")};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#1565c0" : "#e9ecef")};
  }
`;

const Helper = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #dc2626;
  text-align: center;
`;

const DividerContainer = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid #eee;
  padding-top: 24px;
`;
